/**
 *  ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗  █████╗ ██████╗ ██████╗
 *  ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗██╔══██╗██╔══██╗██╔══██╗
 *     ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║███████║██████╔╝██████╔╝
 *     ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║██╔══██║██╔═══╝ ██╔═══╝
 *     ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝██║  ██║██║     ██║
 *     ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝ ╚═╝  ╚═╝╚═╝     ╚═╝
 *
 * (c) Copyright 2018-present Rakuten Kobo Inc. (http://www.kobo.com)
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------

// External/Third-party dependencies
import React from 'react';
import PropTypes from 'prop-types';

// Internal dependencies
import useStyleSystem from '@rakuten/hooks/useStyleSystem';
import { Text } from '@rakuten/modules-core-components';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

/**
 * List of available label types representing a named font style.
 * @type {Object}
 */
const TYPE = {
  CAPTION: "caption",
  HEADER1: "header1",
  HEADER2: "header2",
  MONOSPACE: "monospace",
  TEXT: "text",
  TEXTLARGE: "textLarge",
  TEXTSMALL: "textSmall",
  TEXTXSMALL: "textXSmall",
  TITLE: "title"
};

/**
 * List of available colour variants which correspond to a functional or named
 * colour definition.
 * @type {Object}
 */
const COLOR_VARIANT = {
  ACCENT: 'primary',
  CHARCOAL: 'themeTextCopyCharcoal',
  DARK:  'themeTextCopyDark',
  LABEL: "label",
  ICON:  'icon',
  LIGHT:'themeTextCopyLight',
  SEPIA: 'themeTextCopySepia',
  TEXT: 'text',
  WHITE: 'white'
};

/**
 * List of available colour variants for the disabled state
 * which correspond to a functional or named colour definition.
 * @type {Object}
 */
const DISABLED_VARIANT = {
  PRIMARY: 'primaryDisabled',
  ICON:  'iconDisabled'
};

/**
 * List of available font weights which correspond to a CSS font weight. Only
 * works if either a variable font is used or if the file with the requested
 * font weight is available
 * @type {object}
 */
const FONT_WEIGHT = {
  BOLD: 'bold',
  NORMAL: 'normal',
  THIN: 'thin'
};

/**
 * List of available alignments for the label's text within its container
 * @type {Object}
 */
const TEXT_ALIGNMENT = {
  AUTO: 'auto',
  CENTER: 'center',
  JUSTIFY: 'justify',
  LEFT: 'left',
  RIGHT: 'right'
};


// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

/**
 * Calculate styles for this component, optionally based on props, theme,
 * currently matching breakpoint etc.
 *
 * @param {object} args Arguments used for style calculation
 * @param {object} args.props Current props passed to the component
 * @param {object} args.theme Current theme object
 * @return {object} Object that maps style names to style objects
 */
function createStyles({ props, theme }) {
  const { colorVariant, disabled, disabledVariant, ignoreLineHeight, numberOfLines, selectable, type } = props;

  let fontWeight = 400;
  if (props.fontWeight === FONT_WEIGHT.BOLD) {
    fontWeight = 700;
  } else if (props.fontWeight === FONT_WEIGHT.THIN) {
    fontWeight = 100;
  }

  // Calc height
  // TODO: Currently, only rem values are handled -> handle px, % etc.
  const fontProps = theme.mixins.font(type, fontWeight);
  const size = parseFloat(fontProps.fontSize, 10);
  const height = props.minHeight || size * Math.max(1, numberOfLines)
    * (ignoreLineHeight ? 1 : theme.variables.lineHeight) + "rem";
  const lineHeight = props.ignoreLineHeight
    ? theme.mixins.lineHeight(size, 1.25)
    : theme.mixins.lineHeight(size, theme.variables.lineHeight);

  return {
    label: {
      color: disabled ? theme.colors[disabledVariant] : theme.colors[colorVariant],
      display: "inline",
      hyphens: "auto",
      lineHeight,
      margin: 0,
      minHeight: height,
      overflowWrap: "break-word",
      padding: 0,
      textAlign: props.align,
      textDecoration: "none",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      ...theme.mixins.userSelect(disabled ? "none" : selectable),
      ...fontProps,

      // This is only necessary because the font itself is not aligned
      // properly, so everything looks a bit off in terms of horizontal alignment
      // to other UI elements
      // FIXME: Remove after font file is fixed
      marginTop: '-.2rem'
    },

    subtextStyle: {
      ...fontProps,
      color: theme.colors.label,
      // Space between main text and subtext
      marginLeft: theme.mixins.basicUnit(1.25)
    }

  };
}


// -----------------------------------------------------------------------------
// COMPONENT DEFINITION
// -----------------------------------------------------------------------------

function Label(props) {
  // "Hook" this component into the theme/style system
  const [ styles ] = useStyleSystem(
    {
      id: 'Label',
      props,
      styleFn: createStyles
    },
    [
      props.colorVariant,
      props.fontWeight,
      props.disabled,
      props.selectable,
      props.type,
      // h1,...,h6 used for rendering <Label/> as <h1/>...<h6/>
      props.texttype
    ]
  );


  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const style = props.additionalStyles
    ? [ styles.label, props.additionalStyles ]
    : styles.label

  return (
    <Text
      numberOfLines={ props.numberOfLines }
      style={ style }
      testID={ props.testID }
      texttype={ props.texttype }
      lang={ props.lang }
    >
      { props.text ?? props.children }
      { props.subtext &&
        <Text style={ styles.subtextStyle }>
          { props.subtext }
        </Text>
      }
    </Text>

  );
}


// -----------------------------------------------------------------------------
// PROPS VALIDATION
// -----------------------------------------------------------------------------

Label.propTypes = {
  /** A list of additional styles to be added to the label [optional] */
  additionalStyles: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string
  ]),

  /** Where to align the text within its surrounding block level element [optional] */
  align: PropTypes.oneOf(Object.values(TEXT_ALIGNMENT)),

  /** Color variant of the label as a color theme identifier, e.g. "text" [optional] */
  colorVariant: PropTypes.oneOf(Object.values(COLOR_VARIANT)),

  /** Disabled state color variant of the label as a color theme identifier, e.g. "icon" [optional] */
  disabledVariant: PropTypes.oneOf(Object.values(DISABLED_VARIANT)),

  /** Indicator that the label is deactivated or locked */
  disabled: PropTypes.bool,

  // Font weight of the label [optional]
  fontWeight: PropTypes.oneOf(Object.values(FONT_WEIGHT)),

  /**
   * Used to define min height [optional]
   */
  minHeight: PropTypes.string,

  /**
  * values: h1,...,h6 to interpret <Label/> as <h1/>,...,<h6/>
  * instead of <span/> [optional]
  */
  texttype: PropTypes.string,

  /** Whether or not to take the line height into account when calculating the height [optional] */
  ignoreLineHeight: PropTypes.bool,

  /** Content language locale [optional] */
  lang: PropTypes.string,

  /**
   * Used to truncate the text with an ellipsis after computing the text
   * layout, including line wrapping, such that the total number of lines
   * does not exceed this number [optional]
   */
  numberOfLines: PropTypes.number,

  /**
   * Whether or not the text is selectable by the user in order to use native
   * copy and paste functionality [optional]
   */
  selectable: PropTypes.bool,

  /** Subtext to display next to the main text [optional] */
  subtext: PropTypes.string,

  /** ID for test automation [optional] */
  testID: PropTypes.string,

  /** Text content, overriding children [optional] */
  text: PropTypes.string,

  /** Type of the label to apply correct styling [optional] */
  type: PropTypes.oneOf(Object.values(TYPE))
};


Label.defaultProps = {
  align: TEXT_ALIGNMENT.AUTO,
  texttype: 'text',
  colorVariant: COLOR_VARIANT.TEXT,
  disabledVariant: DISABLED_VARIANT.PRIMARY,
  fontWeight: null,
  ignoreLineHeight: false,
  numberOfLines: 1,
  selectable: false,
  subtext: null,
  type: TYPE.TEXT
};


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default Label;
export {
  COLOR_VARIANT,
  DISABLED_VARIANT,
  FONT_WEIGHT,
  TEXT_ALIGNMENT,
  TYPE
}
